import type { CompanyLocationEntry } from '../geocoding/company-location-resolver';
import type { CompanyLocationMapEntry } from '../../components/CompanyLocationMap';
import type { GazetteerLocale } from '../../types/gazetteer';

/**
 * Converts a company's resolved companyLocations (TEC-482 follow-up) into the lat/lng points
 * CompanyLocationMap renders. Each entry's own resolved place (ancestorChain[0], the city, or
 * the country if only that resolved) carries the gazetteer's coordinates, so this needs no
 * separate geocode lookup.
 *
 * The registry-address entry's own formatted address line (already built from the exact
 * registryStreet/registryPostCode/registryCity, more precise than the gazetteer's city-level
 * point) is used as its popup text when provided; secondary (office) entries show the resolved
 * place's own display name instead, since there's no street-level address for those.
 */
export function buildCompanyLocationMapEntries(
  companyLocations: CompanyLocationEntry[] | undefined,
  locale: GazetteerLocale,
  primaryAddressLine?: string | null,
): CompanyLocationMapEntry[] {
  if (!companyLocations || companyLocations.length === 0) return [];

  const entries: CompanyLocationMapEntry[] = [];
  for (const entry of companyLocations) {
    if (entry.resolution.kind !== 'place') continue;
    const place = entry.resolution.ancestorChain?.[0];
    if (!place || typeof place.lat !== 'number' || typeof place.lng !== 'number') continue;

    const address = entry.isPrimary
      ? primaryAddressLine || place.displayNames?.[locale] || place.name
      : place.displayNames?.[locale] || place.name;

    entries.push({ lat: place.lat, lng: place.lng, address, isPrimary: entry.isPrimary });
  }

  return entries;
}
